import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Snackbar from '@material-ui/core/Snackbar'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import { useNavigate, useParams } from 'react-router-dom'

import { useLogin } from '../../common/LoginContext'
import { getTeacherResources } from '../../common/utils'
import { peekListing, refreshListing, storedListing } from './listingCache'
import { peekFile, peekUrl, storeFile, prefetchDownloadUrl, prefetchFiles } from './fileCache'
import { appHeaderHeight } from '../../routes/ClassDashboardSiteHeader'

import { CodeViewerDialog } from './CodeViewerDialog'
import { FolderRail } from './FolderRail'
import { PackRail } from './PackRail'
import { ResourceGroups } from './ResourceGroups'
import { CATEGORIES } from './categories'
import { HEADER_HEIGHT } from './layout'
import { foldersOf, initialFor, packBySlug, tintFor } from './folders'
import {
  buildTree,
  documentTotalOf,
  folderBySlug,
  packRootOf,
  sectionsFor,
  solutionsTotalOf,
  subtreeFiles,
} from './tree'

const useStyles = makeStyles(theme => ({
  page: {
    position: 'relative',
    display: 'flex',
    height: `calc(100vh - ${appHeaderHeight})`,
    textAlign: 'left',
    // Root paints custom.background (#2B2B2B) behind every page, even in
    // light mode. Pages that don't cover it with their own surface sit on
    // charcoal, which reads as a stuck dialog backdrop. The approved mock
    // puts the whole app on white.
    backgroundColor: theme.palette.background.paper,
  },
  main: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  // The header is a sibling of the scroll area, not inside it, so the rule
  // under it stays put and lines up with the two rails' rules at every scroll
  // position. All three bands share HEADER_HEIGHT for exactly that reason.
  mainHead: {
    height: HEADER_HEIGHT,
    boxSizing: 'border-box',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0, 3),
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  mainBody: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1, 3, 2),
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  centered: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  '@keyframes slide': {
    '0%': { left: '-35%' },
    '100%': { left: '100%' },
  },
  topbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    overflow: 'hidden',
    backgroundColor: '#dcedc8',
    zIndex: 2,
    '& span': {
      position: 'absolute',
      height: '100%',
      width: '35%',
      backgroundColor: theme.palette.secondary.main,
      animation: '$slide 1.2s ease-in-out infinite',
    },
  },
  fetchPill: {
    position: 'absolute',
    top: 12,
    right: 16,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(0, 0, 0, 0.15)',
    borderRadius: 999,
    padding: '5px 12px',
    boxShadow: '0 1px 5px rgba(0, 0, 0, 0.08)',
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.45 },
    '100%': { opacity: 1 },
  },
  bone: {
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.09)',
    animation: '$pulse 1.6s ease-in-out infinite',
  },
  railBone: {
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    animation: '$pulse 1.6s ease-in-out infinite',
  },
  railSkeleton: {
    width: 280,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.custom.background,
  },
  railSkeletonHead: {
    height: HEADER_HEIGHT,
    boxSizing: 'border-box',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
  },
  railSkeletonBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  cardBoneGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
  },
  cardBone: {
    height: 96,
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  rowBone: { height: 56, borderRadius: 6, marginBottom: theme.spacing(1) },
  emptyCopy: { maxWidth: 460 },
  section: {
    scrollMarginTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0, 1),
  },
  sectionChip: {
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    fontSize: '0.62rem',
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: 'var(--tint)',
  },
}))

// The listing build can take several seconds server-side on a cold path (F24);
// past this threshold a bare spinner reads as "stuck", so the page says more.
export const SLOW_HINT_MS = 8000

function EducatorResourcesPage() {
  const classes = useStyles()
  const [loginState] = useLogin()
  const user = loginState?.user

  const [listing, setListing] = React.useState(null)
  const [loadError, setLoadError] = React.useState(null)
  const [busyPath, setBusyPath] = React.useState(null)
  const [viewerFile, setViewerFile] = React.useState(null)
  const [fileError, setFileError] = React.useState(null)
  const [slowHint, setSlowHint] = React.useState(false)
  const [fetching, setFetching] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  // The URL is the selection (D26). Both rails write to it and read back from
  // it, so a bookmark, a refresh, and a link Kathy pasted all restore the same
  // view without any extra state to keep in sync. Under A2 the URL is also the
  // rail's expansion state, since the open branch is exactly the active path
  // (D32) - so there is no second copy of "where am I" to drift.
  const params = useParams()
  const packSlug = params.packSlug
  const folderPath = params['*'] || ''
  const navigate = useNavigate()
  const paneRef = React.useRef(null)

  // Surface reassurance if the first load runs long (see SLOW_HINT_MS above).
  React.useEffect(() => {
    if (listing || loadError) {
      return undefined
    }
    const timer = setTimeout(() => setSlowHint(true), SLOW_HINT_MS)
    return () => clearTimeout(timer)
  }, [listing, loadError])

  // Selection lives in the URL now, so a refreshed listing has nothing to
  // clobber: an absent or stale slug resolves to the first pack or folder at
  // render time instead of being written into state on first paint.
  const applyListing = (data) => {
    setListing(data)
  }

  // One `list` call for the whole tree, shared through listingCache: Root
  // primes it at login, so by the time this page mounts the fetch is usually
  // already under way - or done. Category switching and search stay
  // client-side over what it returns.
  React.useEffect(() => {
    let cancelled = false
    if (!user) {
      return undefined
    }
    // Serve whatever is in hand instantly (memory first, then the stored
    // copy), and revalidate in the background so a license gained or lost
    // shows up on the next visit to this page - not only after a reload.
    // refreshListing trusts data younger than its freshness window, so
    // flipping between dashboard sections stays free.
    const known = peekListing(user) || storedListing(user)
    if (known) {
      applyListing(known)
    }
    const pending = refreshListing(user)
    if (!pending) {
      return undefined
    }
    setFetching(true)
    ;(async () => {
      try {
        const data = await pending
        if (!cancelled) {
          applyListing(data)
        }
      } catch (err) {
        if (!cancelled && !known) {
          setLoadError('We could not load your resources. Please try again.')
        }
      } finally {
        if (!cancelled) {
          setFetching(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  const packs = listing?.packs || []
  const pack = packBySlug(packs, packSlug)

  // Memoised on the pack, not on the folder array: `foldersOf` returns a fresh
  // array every render, so keying the tree to it would rebuild on every
  // keystroke in the search box.
  const roots = React.useMemo(() => buildTree(foldersOf(pack, CATEGORIES)), [pack])
  // Only the first segment names a view. Nesting is capped at three levels
  // (D33) and both levels below a folder render on the folder's own page, so a
  // deeper URL — a link minted while the rail was a tree — resolves to the
  // folder it starts with rather than 404ing.
  const folderSlug = folderPath.split('/').filter(Boolean)[0] || ''
  const activeFolder = folderBySlug(roots, folderSlug)
  // No folder is the pack itself, not an error. Rendering the pack through the
  // same node shape keeps the landing view showing every folder at once while
  // the folder view is the identical component one level down.
  const activeNode = activeFolder || packRootOf(roots, pack?.name)

  // Once a pack is on screen, quietly pull its inline files (code solutions,
  // answer keys, mission logs) into the file cache so the viewer opens
  // instantly on click. Download files are excluded on purpose: their signed
  // URLs are minted per click so at most one ticket is ever live.
  React.useEffect(() => {
    if (!user || !pack) {
      return undefined
    }
    const files = []
    for (const root of roots) {
      files.push(...subtreeFiles(root))
    }
    return prefetchFiles(user, files)
  }, [user, pack, roots])

  const goTo = (nextPackId, slugs) => {
    const tail = slugs && slugs.length > 0 ? `/${slugs.join('/')}` : ''
    navigate(`/educator-resources/${String(nextPackId).toLowerCase()}${tail}`)
  }

  // Also the way back to the whole-pack view: the pack rail row stays
  // clickable while selected, and selecting a pack always lands on its
  // overview rather than remembering a folder. That is why the page carries no
  // breadcrumb - with one folder level there was nothing for it to say that
  // the rail's own highlight does not.
  const selectPack = (packId) => {
    goTo(packId, null)
  }

  // Picking a folder replaces the pane rather than scrolling to it: the
  // whole-pack view summarises every folder, so the section a teacher wants is
  // rarely the one already on screen, and scrolling to it would leave the other
  // five hundred files loaded above.
  const selectFolder = (node) => {
    goTo(pack?.id, node.slugs)
    const pane = paneRef.current
    if (pane && typeof pane.scrollTo === 'function') {
      pane.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const copyFolderLink = async (node) => {
    const url =
      `${window.location.origin}/educator-resources/` +
      `${String(pack?.id).toLowerCase()}/${node.slugs.join('/')}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch (err) {
      // A denied clipboard permission is not worth an error dialog; the link
      // is reconstructable by clicking the folder and reading the address bar.
      setCopied(false)
    }
  }

  // The dialog opens on the click, not after the fetch - the contents round
  // trip takes a moment, and a click with no visible response reads as
  // broken. The fetch fills the open dialog in; closing it mid-fetch stays
  // closed, and a second click retargets the dialog to the newer file.
  const openFile = async (file) => {
    if (file.serve === 'inline') {
      // The prefetcher usually got here first - open straight from cache,
      // with no loading state and no request.
      const cached = peekFile(user, file.path)
      if (cached) {
        setViewerFile({ ...file, contents: cached.contents, meta: cached.meta })
        return
      }
      setViewerFile({ ...file, loading: true })
    } else {
      // Hovering the button usually minted the ticket already, so the click
      // navigates with no round trip at all.
      const ticket = peekUrl(user, file.path)
      if (ticket) {
        window.location.assign(ticket)
        return
      }
      setBusyPath(file.path)
    }
    setFileError(null)
    const closeIfStillOpen = () => {
      setViewerFile(prev => (prev && prev.path === file.path ? null : prev))
    }
    try {
      const resp = await getTeacherResources(
        `file/${file.path.split('/').map(encodeURIComponent).join('/')}`,
        user
      )
      if (!resp || typeof resp.json !== 'function' || !resp.ok) {
        closeIfStillOpen()
        setFileError(
          resp?.status === 403 ?
            'Your license does not cover this pack.' :
            'That file could not be opened.'
        )
        return
      }
      const data = await resp.json()
      if (data.serve === 'inline') {
        storeFile(user, file.path, data)
        setViewerFile(prev => (
          prev && prev.path === file.path ?
            { ...file, contents: data.contents, meta: data.meta } :
            prev
        ))
        return
      }
      // The server decides delivery, so follow it even if the listing said
      // inline. Navigate to the signed URL rather than fetching it: a fetch
      // would trigger a CORS preflight against the bucket, which is not
      // configured for one. Minted on click, so only one ticket is ever live.
      closeIfStillOpen()
      window.location.assign(data.url)
    } catch (err) {
      closeIfStillOpen()
      setFileError('That file could not be opened.')
    } finally {
      setBusyPath(null)
    }
  }

  // Variant C (mocks/educator-resources-loading-alternatives.html): a
  // sweeping top bar plus a corner pill, shown whenever a listing fetch is
  // in flight for this page - the cold first load and every background
  // revalidation alike.
  const fetchIndicator = fetching ? (
    <>
      <div className={classes.topbar} aria-hidden='true'><span /></div>
      <div className={classes.fetchPill} role='status'>
        <CircularProgress size={12} thickness={5} color='secondary' />
        <span>
          {!listing && slowHint ?
            'Fetching curriculum \u2014 the first load can take a moment' :
            'Fetching curriculum'}
        </span>
      </div>
    </>
  ) : null

  if (loadError) {
    return (
      <div className={classes.page}>
        <div className={classes.centered}>
          <Typography variant='h6'>{'Something went wrong'}</Typography>
          <Typography variant='body2' color='textSecondary'>{loadError}</Typography>
        </div>
      </div>
    )
  }

  // While the listing builds, show the page's real frame — rail, heading,
  // category cards — as pulsing placeholders, so content populates in place
  // instead of jumping from a centered spinner to a finished layout.
  if (!listing) {
    return (
      <div className={classes.page}>
        {fetchIndicator}
        <div className={classes.railSkeleton} aria-hidden='true'>
          <div className={classes.railSkeletonHead}>
            <div className={classes.railBone} style={{ width: 118, height: 14, marginBottom: 8 }} />
            <div className={classes.railBone} style={{ width: 86, height: 10 }} />
          </div>
          <div className={classes.railSkeletonBody}>
            {[0, 1, 2].map(i => (
              <div key={i} className={classes.railBone} style={{ height: 52 }} />
            ))}
          </div>
        </div>
        <div className={classes.main}>
          {/* The skeleton wears the same header band as the loaded page, so
              the rules across the three columns are already in place and do
              not jump into line when the listing arrives. */}
          <div className={classes.mainHead} aria-hidden='true'>
            <div className={classes.bone} style={{ width: 260, height: 22, marginBottom: 8 }} />
            <div className={classes.bone} style={{ width: 340, height: 13 }} />
          </div>
          <div className={classes.mainBody} aria-hidden='true'>
            <div className={classes.cardBoneGrid}>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className={classes.cardBone}>
                  <div className={classes.bone} style={{ width: '40%', height: 12 }} />
                  <div className={classes.bone} style={{ width: '70%', height: 14 }} />
                  <div className={classes.bone} style={{ width: '90%', height: 10 }} />
                </div>
              ))}
            </div>
            {[0, 1, 2].map(i => (
              <div key={i} className={`${classes.bone} ${classes.rowBone}`} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  // A teacher with no active license sees why, and where to fix it — the
  // no-seats copy in AddGroupDialog is the precedent for pointing at the
  // license dashboard rather than dead-ending.
  if (!pack) {
    return (
      <div className={classes.page}>
        {fetchIndicator}
        <div className={classes.centered}>
          <Typography variant='h6'>{'No curriculum licenses yet'}</Typography>
          <Typography variant='body2' color='textSecondary' className={classes.emptyCopy}>
            {'Educator resources — code solutions, answer keys, assessments and teacher ' +
           'guides — are available for each curriculum pack you hold an active license for.'}
          </Typography>
          <Button
            variant='contained'
            color='primary'
            href='https://license.firialabs.com'
            target='_blank'
            rel='noopener noreferrer'
          >
            {'Go to license dashboard'}
          </Button>
        </div>
      </div>
    )
  }

  const sections = sectionsFor(activeNode)

  return (
    <div className={classes.page}>
      {fetchIndicator}
      <PackRail packs={packs} selectedPackId={pack.id} onSelectPack={selectPack} />
      <FolderRail
        roots={roots}
        activeSlug={activeFolder?.slug || null}
        onSelectFolder={selectFolder}
        onCopyLink={copyFolderLink}
      />

      <div className={classes.main}>
        <div className={classes.mainHead}>
          <div className={classes.head}>
            <Typography variant='h6'>{pack.name}</Typography>
          </div>
          <Typography variant='caption' color='textSecondary'>
            {`${solutionsTotalOf(roots)} code solutions and ${documentTotalOf(roots)} teacher documents`}
          </Typography>
        </div>

        <div className={classes.mainBody} ref={paneRef}>
          {sections.length === 0 ?
            <Typography variant='body2' color='textSecondary'>
              {'Nothing has been published for this pack yet.'}
            </Typography> :
            sections.map(section => (
              <section key={section.key} className={classes.section}>
                <div className={classes.sectionHead}>
                  <span
                    className={classes.sectionChip}
                    style={{ '--tint': tintFor(section.slugs[0]) }}
                  >
                    {section.generated ? '</>' : initialFor(section.name)}
                  </span>
                  <Typography variant='subtitle1' component='h2'>
                    {/* Only a folder has a page to go to. On the whole-pack
                      view every section is one, so the heading is the way in;
                      inside a folder the sections are collections that already
                      render in full right here, and a link would go nowhere. */}
                    {section.slugs.length === 1 ?
                      <Link
                        component='button'
                        type='button'
                        variant='subtitle1'
                        color='inherit'
                        onClick={() => goTo(pack.id, section.slugs)}
                      >
                        {section.name}
                      </Link> :
                      section.name}
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    {`${section.count} file${section.count === 1 ? '' : 's'}`}
                  </Typography>
                </div>
                {section.groups.length === 0 ?
                  <Typography variant='body2' color='textSecondary'>
                    {`${section.name} is empty.`}
                  </Typography> :
                  <ResourceGroups
                    groups={section.groups}
                    busyPath={busyPath}
                    onOpenFile={openFile}
                    onIntentFile={file => prefetchDownloadUrl(user, file)}
                  />}
              </section>
            ))}
        </div>
      </div>

      <CodeViewerDialog file={viewerFile} onClose={() => setViewerFile(null)} />

      <Snackbar
        open={Boolean(fileError)}
        message={fileError || ''}
        autoHideDuration={6000}
        onClose={() => setFileError(null)}
      />

      <Snackbar
        open={copied}
        message='Link copied'
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
      />
    </div>
  )
}

export { EducatorResourcesPage }
